<template>
  <Loading v-if="isLoading" />
  <div v-else>
    <vs-prompt
      title="Create new meeting"
      @accept="onAcceptCreateNew"
      :active.sync="isCreateNewPromptActive"
      :is-valid="validForm"
      accept-text="Add"
    >
      <div class="flex flex-col gap-2 w-full max-w-md">
        <vs-input label="Title" v-model="title" class="w-full" />
        <vs-select label="Repeat frequency type" v-model="type" class="w-full">
          <vs-select-item
            :key="frequencyType"
            v-for="frequencyType in meetingFrequencyTypes"
            :value="frequencyType"
            :text="frequencyType"
          />
        </vs-select>
        <div class="w-full">
          <label class="vs-input--label">Allowed Venues</label>
          <VenueSelector 
            v-model="allowedVenueIds" 
            :multiple="true" 
            :filterCountries="[activeCountry.name]"
            placeholder="Select venues that can take this meeting"
            class="w-full"
          />
        </div>
      </div>
    </vs-prompt>

    <div class="w-full flex mb-3 justify-between">
      <p>
        There are {{ rules.length }} notification rules.
        <span v-if="hasPermission('vp.life.meetings.managerules')"
          >Click <vs-button size="small" @click.stop="manageRules" class="px-2 py-1">here</vs-button> to manage</span
        >
      </p>
      <div class="flex gap-2">
        <vs-button type="border" @click="isVenueOverviewActive = true">Venue overview</vs-button>
        <vs-button @click="isCreateNewPromptActive = true">Create new</vs-button>
      </div>
    </div>

    <vs-popup fullscreen title="Meetings per venue" :active.sync="isVenueOverviewActive">
      <vs-table :data="venueOverview" search max-items="50" pagination>
        <template slot="thead">
          <vs-th sort-key="name">Venue</vs-th>
          <vs-th sort-key="meetingCount">Meetings</vs-th>
          <vs-th v-for="meeting in meetings" :key="meeting.id">{{ meeting.title }}</vs-th>
        </template>
        <template slot-scope="{ data }">
          <vs-tr v-for="row in data" :key="row.venueId" :data="row">
            <vs-td>{{ row.name }}</vs-td>
            <vs-td>
              <vs-chip :color="row.meetingCount > 0 ? 'success' : 'danger'">{{ row.meetingCount }}</vs-chip>
            </vs-td>
            <vs-td v-for="meeting in meetings" :key="meeting.id">
              <feather-icon
                v-if="row.meetingIds.includes(meeting.id)"
                icon="CheckIcon"
                svgClasses="w-5 h-5 text-success"
              />
              <span v-else class="text-gray-400">-</span>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vs-popup>

    <div v-if="!isLoading" class="data-list-list-view">
      <vs-table
        ref="table"
        class="mt-8"
        v-model="selected"
        pagination
        maxItems="10"
        search
        :data="meetings"
        @selected="onClicked(selected.id)"
      >
        <div slot="header" class="flex flex-wrap-reverse items-center flex-grow justify-between">
          <div class="flex flex-wrap-reverse items-center data-list-btn-container">
            <!-- ADD NEW -->
          </div>

          <!-- ITEMS PER PAGE -->
        </div>
        <template slot="thead">
          <vs-th sort-key="title">Title</vs-th>
          <vs-th sort-key="meeting.allowedVenueIds">Allowed Venues</vs-th>
          <vs-th sort-key="meeting.type">Repeat frequency</vs-th>
          <vs-th sort-key="meeting.enabled">Enabled</vs-th>
        </template>

        <template slot-scope="{ data }">
          <tbody>
            <vs-tr :data="tr" :key="tr.id" v-for="tr in data">
              <vs-td>
                <div class="product-name font-medium truncate flex flex-row items-center">
                  <p class="ml-4">
                    {{ tr.title }}
                  </p>
                </div>
              </vs-td>
              <vs-td>
                <div v-if="tr.allowedVenueIds && tr.allowedVenueIds.length > 0">
                  <span class="text-primary font-medium">{{ tr.allowedVenueIds.length }} selected</span>
                </div>
                <span v-else class="text-gray-400">No venues selected</span>
              </vs-td>
              <vs-td>
                {{ tr.type }}
              </vs-td>
              <vs-td>
                <vs-chip :color="tr.enabled | yesnocolor">
                  {{ tr.enabled | yesno }}</vs-chip
                >
              </vs-td>
            </vs-tr>
          </tbody>
        </template>
      </vs-table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import _ from "lodash";
import VenueSelector from "@/components/VenueSelector.vue";

export default {
  components: { VenueSelector },
  data() {
    return {
      selected: null,

      isCreateNewPromptActive: false,
      isVenueOverviewActive: false,
      title: "",
      type: null,
      allowedVenueIds: []
    };
  },
  methods: {
    async onAcceptCreateNew() {
      try {
        await this.$store.dispatch("meetings/createNewMeeting", {
          title: this.title,
          frequencyType: this.type,
          country: this.activeCountry.id,
          allowedVenueIds: this.allowedVenueIds
        });
        this.$vs.notify({
          title: "Success",
          text: "Meeting created",
          color: "green"
        });

        this.title = "";
        this.type = null;
        this.allowedVenueIds = [];
        this.isCreateNewPromptActive = false;
        await this.$store.dispatch("meetings/getAll", { country: this.activeCountry.id });
      } catch (err) {
        this.$vs.notify({
          title: "Error",
          text: "Could not create meeting",
          color: "danger"
        });
      }
    },
    onClicked(meetingId) {
      this.$router.push({ name: "manage-meeting", params: { id: meetingId } });
    },
    manageRules() {
      this.$router
        .push({
          name: "manage-meeting-rules"
        })
        .catch(() => {});
    }
  },
  computed: {
    ...mapGetters({
      isLoading: "operationLinks/loading",
      activeCountry: "venues/activeCountry",
      meetings: "meetings/all",
      meetingFrequencyTypes: "meetings/meetingFrequencyTypes",
      rules: "meetingRules/rules",
      venues: "venues/venues"
    }),
    venueOverview() {
      const meetings = this.meetings || [];
      return (this.venues || [])
        .filter(v => v.status === 0 || v.status === 2)
        .filter(v => v.country === this.activeCountry.name)
        .map(v => {
          const meetingIds = meetings.filter(m => (m.allowedVenueIds || []).includes(v.venueId)).map(m => m.id);
          return { venueId: v.venueId, name: v.name, meetingIds, meetingCount: meetingIds.length };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    validForm() {
      return this.title !== "" && this.type !== null && this.allowedVenueIds && this.allowedVenueIds.length > 0;
    }
  },
  async mounted() {
    const activeCountry = this.$store.getters["venues/activeCountry"];
    await this.$store.dispatch("meetings/getAll", { country: activeCountry.id });
    await this.$store.dispatch("meetingRules/getRules", activeCountry.id);
    // await this.$store.dispatch("operationLinks/getAllByCountry", this.activeCountry.id);
    // console.log("Got operation links by country", this.operationLinks);
  }
};
</script>

<style lang="scss">
</style>